import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { JobsService } from '../services/jobsService';
import { AuthService } from '../services/authService';
import { JobWithClient, WeeklySummary } from '../types/jobs';
import JobSchedule from '../components/JobSchedule';
import ActiveSession from '../components/ActiveSession';
import TimeSummary from '../components/TimeSummary';
import QRScanner from '../components/QRScanner';

const ProviderDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  
  const [todayJobs, setTodayJobs] = useState<JobWithClient[]>([]);
  const [upcomingJobs, setUpcomingJobs] = useState<JobWithClient[]>([]);
  const [activeSession, setActiveSession] = useState<any>(null);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary>({
    totalHours: 0,
    jobsCompleted: 0,
    jobsScheduled: 0,
    averageHoursPerDay: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showQRScanner, setShowQRScanner] = useState(false);

  useEffect(() => {
    if (user?.userId) {
      loadDashboardData();
    }
  }, [user?.userId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load active session
      const sessionResponse = await AuthService.getActiveSession();
      if (sessionResponse.success && sessionResponse.data?.activeSession) {
        setActiveSession(sessionResponse.data.activeSession);
      }

      // Load today's jobs
      const todayResponse = await JobsService.getTodayJobs(user!.userId);
      if (todayResponse.success) {
        setTodayJobs(todayResponse.jobs);
      }

      // Load upcoming jobs (next 7 days)
      const upcomingResponse = await JobsService.getWeekJobs(user!.userId);
      if (upcomingResponse.success) {
        // Filter for upcoming jobs (not today)
        const today = new Date().toISOString().split('T')[0];
        const upcoming = upcomingResponse.jobs.filter(job => 
          job.expectedStartTime && 
          job.expectedStartTime.split('T')[0] > today
        );
        setUpcomingJobs(upcoming);
      }

      // Calculate weekly summary
      calculateWeeklySummary(todayResponse.jobs, upcomingResponse.jobs);

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklySummary = (todayJobs: JobWithClient[], weekJobs: JobWithClient[]) => {
    const completedJobs = todayJobs.filter(job => job.status === 'completed');
    const scheduledJobs = weekJobs.filter(job => job.status === 'active');
    
    // Calculate total hours (simplified - in real app you'd get this from work records)
    const totalHours = completedJobs.reduce((total, job) => {
      return total + (job.expectedDuration || 0);
    }, 0);

    const summary: WeeklySummary = {
      totalHours,
      jobsCompleted: completedJobs.length,
      jobsScheduled: scheduledJobs.length,
      averageHoursPerDay: totalHours / 7 // Simplified calculation
    };

    setWeeklySummary(summary);
  };

  const handleEndSession = async () => {
    try {
      // This would call the check-out API
      // For now, we'll just clear the active session
      setActiveSession(null);
      
      // Reload dashboard data
      await loadDashboardData();
    } catch (err) {
      console.error('Error ending session:', err);
      setError(t('somethingWentWrong'));
    }
  };

  const handleConfirmCheckOut = async () => {
    try {
      // This would call the check-out API
      // For now, we'll just clear the active session
      setActiveSession(null);
      
      // Reload dashboard data
      await loadDashboardData();
    } catch (err) {
      console.error('Error confirming check-out:', err);
      setError(t('somethingWentWrong'));
    }
  };

  const handleJobClick = (job: JobWithClient) => {
    // Navigate to job details or handle job selection
    console.log('Job clicked:', job);
  };

  const handleScanQRCode = () => {
    setShowQRScanner(true);
  };

  const handleQRScan = async (qrData: string) => {
    try {
      setShowQRScanner(false);
      
      // Parse QR data (format: https://app.com/checkin/{clientUuid}/{timestamp})
      const url = new URL(qrData);
      const pathParts = url.pathname.split('/');
      const clientUuid = pathParts[2];
      const timestamp = pathParts[3];
      
      // Start session
      const sessionData = {
        clientId: clientUuid,
        providerId: user!.userId,
        timestamp: Date.now().toString()
      };
      
      const response = await AuthService.startSession(sessionData);
      if (response.success) {
        // Reload dashboard to show active session
        await loadDashboardData();
      } else {
        setError(response.error || t('somethingWentWrong'));
      }
    } catch (err) {
      console.error('Error processing QR code:', err);
      setError(t('somethingWentWrong'));
    }
  };

  const handleQRCancel = () => {
    setShowQRScanner(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('providerDashboard')}</h1>
              <p className="text-gray-600">{t('welcome')}, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="btn-secondary"
            >
              {t('signOut')}
            </button>
          </div>
        </div>
      </header>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Active Session or Check-in Section */}
            {activeSession ? (
              <ActiveSession
                session={activeSession}
                onEndSession={handleEndSession}
                onConfirmCheckOut={handleConfirmCheckOut}
              />
            ) : (
              <div className="card">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {t('checkInCheckOut')}
                </h2>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 font-medium">{t('notCurrentlyCheckedIn')}</p>
                    <p className="text-blue-600 text-sm mt-1">
                      {t('scanClientQRCode')}
                    </p>
                  </div>
                  <button 
                    onClick={handleScanQRCode}
                    className="btn-primary w-full"
                  >
                    {t('scanQRCode')}
                  </button>
                </div>
              </div>
            )}

            {/* Today's Schedule */}
            <JobSchedule
              jobs={todayJobs}
              title={t('todaysSchedule')}
              emptyMessage={t('noJobsToday')}
              onJobClick={handleJobClick}
            />

            {/* Upcoming Jobs */}
            <JobSchedule
              jobs={upcomingJobs}
              title={t('upcomingJobs')}
              emptyMessage={t('noScheduledJobs')}
              onJobClick={handleJobClick}
            />

            {/* Time Summary */}
            <TimeSummary summary={weeklySummary} />

            {/* Provider Info */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('yourInformation')}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('name')}</label>
                  <p className="text-gray-900">{user?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('email')}</label>
                  <p className="text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('status')}</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user?.status === 'approved' ? t('approved') : t('pending')}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('quickActions')}
              </h2>
              <div className="space-y-3">
                <button className="btn-primary w-full">
                  {t('viewFullSchedule')}
                </button>
                <button className="btn-secondary w-full">
                  {t('viewTimeReports')}
                </button>
                <button className="btn-secondary w-full">
                  {t('updateProfile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* QR Scanner Modal */}
      {showQRScanner && (
        <QRScanner
          onScan={handleQRScan}
          onCancel={handleQRCancel}
        />
      )}
    </div>
  );
};

export default ProviderDashboard; 